// OWNED BY the tasks implementation agent — only that agent edits this file
// (plus files it creates under detail/tabs/tasks/). Everything else under
// detail/ (registry, tab strip, header, page shell, sessionCache) is frozen
// shared foundation — see docs/crm/customer-page-implementation.md (server repo).
//
// משימות tab: tasks grouped באיחור / היום / השבוע / בהמשך / הושלמו with
// checkbox completion and postpone actions; creation via a simple "משימה
// חדשה" button (same pattern as MeetingsTab) opening the global calendar
// editor in task mode, prefilled with this contact. The old quick-add
// composer (free-text row + due chip + voice + suggestion chips) was removed
// by design feedback (2026-08) — one obvious button beats a busy composer.
// Data = the user's own calendar events (CalendarContext onSnapshot,
// session-cache warmed fallback), filtered to this contact.

import { useEffect } from "react";
import {
  Box,
  Stack,
  Button,
  Skeleton,
  Typography
} from "@material-ui/core";
import { Add, Today } from "@material-ui/icons";
import EmptyContent from "components/EmptyContent";
import useAuth from "hooks/useAuth";
import useCalendar from "hooks/useCalendar";
import { useSnackbar } from "notistack5";
import { db } from "firebaseConfig";
import { adjustTaskTimeForToday, parseContactsToAttendees } from "utils/calendar/helper";
import { TabCard } from "../TabCard";
import { useContactTasks } from "./tasks/useContactTasks";
import { ContactTaskRow } from "./tasks/ContactTaskRow";
import {
  eventDocRef,
  TASK_GROUP_ORDER,
  TASK_GROUP_LABELS,
  isOwnCalendarEvent,
  warmContactTasksCache
} from "./tasks/contactTasksData";
import type { CalendarEvent, RealEstateContact } from "../../../../../@types/sharedSchema";
import type { ContactTabComponentProps, TabBadge } from "./tabContract";

export default function TasksTab({ contact }: ContactTabComponentProps): JSX.Element {
  const { user } = useAuth();
  const { showSingleEventEditorModal, showSingleEventEditorModalByType, initiateLoadEvents } = useCalendar();
  const { enqueueSnackbar } = useSnackbar();

  // Start the own-calendar live listener when the tab is actually opened —
  // NOT from useContactTasks (the badge hook shares it and badge hooks must
  // never start fetches/listeners). CalendarContext dedups internally.
  useEffect(() => {
    initiateLoadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { loading, live, groups, openCount, overdueCount } = useContactTasks(contact);

  const userEmail: string = user?.email ?? "";

  const hasAnyTask = openCount > 0 || groups.done.length > 0;

  const openEditor = (event: CalendarEvent): void => {
    // The global editor dialog (mounted by CalendarContext) looks the event up
    // in the live context events — only usable once they are loaded.
    if (!live || !event.id) return;
    showSingleEventEditorModal(event.id);
  };

  const handleNewTask = (): void => {
    // Same prefill convention as MeetingsTab / the page header: the global
    // calendar editor in task mode, attendee-prefilled with this contact.
    showSingleEventEditorModalByType?.("task", {
      attendees: parseContactsToAttendees([contact])
    });
  };

  // Ported from CalendarTasks.handleMoveAllOverdueTasksToToday, hardened to
  // use event.path refs and to skip non-own events (shared calendars).
  const handleMoveAllOverdueToToday = async (): Promise<void> => {
    const ownOverdueTasks = groups.overdue.filter(
      (task) => isOwnCalendarEvent(task, userEmail) && !!task.id
    );
    if (!ownOverdueTasks.length) return;

    const batch = db.batch();

    for (const task of ownOverdueTasks) {
      const { start, end } = adjustTaskTimeForToday(new Date(task.start), new Date(task.end));
      batch.update(eventDocRef(task, userEmail), { start, end });
    }

    try {
      await batch.commit();
    } catch {
      enqueueSnackbar("העברת המשימות להיום נכשלה", { variant: "error", autoHideDuration: 4000 });
    }
  };

  return (
    <Stack gap={2.5} sx={{ maxWidth: 880, mx: "auto", width: "100%" }}>
      <TabCard>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" alignItems="baseline" gap={1} sx={{ minWidth: 0 }}>
            <Typography variant="h6">משימות</Typography>
            {!loading && (
              <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
                {openCount} פתוחות
                {overdueCount > 0 ? ` · ${overdueCount} באיחור` : ""}
              </Typography>
            )}
          </Stack>
          <Button size="small" variant="contained" startIcon={<Add />} onClick={handleNewTask}>
            משימה חדשה
          </Button>
        </Stack>

        {loading ? (
          <TasksSkeleton />
        ) : hasAnyTask ? (
          TASK_GROUP_ORDER.map((groupId) => {
            const groupTasks = groups[groupId];
            if (!groupTasks.length) return null;

            const isOverdueGroup = groupId === "overdue";

            return (
              <Box key={groupId}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    mt: 2.5,
                    mb: 0.5,
                    pb: 0.5,
                    borderBottom: (theme) => `1px dashed ${theme.palette.divider}`
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: isOverdueGroup ? "error.main" : "text.secondary" }}
                  >
                    {TASK_GROUP_LABELS[groupId]} · {groupTasks.length}
                  </Typography>
                  {isOverdueGroup && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Today />}
                      onClick={() => void handleMoveAllOverdueToToday()}
                    >
                      העבר הכול להיום
                    </Button>
                  )}
                </Stack>
                {groupTasks.map((task) => (
                  <ContactTaskRow
                    key={task.id ?? `${task.title}-${String(task.start)}`}
                    task={task}
                    group={groupId}
                    live={live}
                    onEdit={openEditor}
                  />
                ))}
              </Box>
            );
          })
        ) : (
          <Box sx={{ mt: 1 }}>
            <EmptyContent
              title="אין משימות ללקוח"
              description="הוסיפו משימה ראשונה בכפתור ״משימה חדשה״ למעלה — היא תופיע גם ביומן וגם כאן."
            />
          </Box>
        )}
      </TabCard>
    </Stack>
  );
}

function TasksSkeleton(): JSX.Element {
  return (
    <Stack gap={1.5} sx={{ mt: 2.5 }}>
      {[0, 1, 2].map((index) => (
        <Stack key={index} direction="row" alignItems="center" gap={1.5}>
          <Skeleton variant="circular" width={20} height={20} />
          <Stack sx={{ flexGrow: 1 }}>
            <Skeleton width="45%" height={20} />
            <Skeleton width="25%" height={16} />
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

// Badge for the tab strip: count of open (pending) tasks for this contact;
// turns red when any of them is overdue. Reads only from CalendarContext's
// onSnapshot state / the session cache — no per-render fetching.
export function useTasksTabBadge(contact: RealEstateContact): TabBadge {
  const { openCount, overdueCount } = useContactTasks(contact);

  if (!openCount) return null;

  return { count: openCount, color: overdueCount > 0 ? "error" : "default" };
}

// Cache warm-up fired on contacts-table row hover and page mount: one-shot
// fetch of the user's own calendar into the session cache (keyed per user, so
// hovering many rows dedups to a single fetch; skipped entirely once any
// snapshot — fetched or live-mirrored — exists). Never throws.
export function preloadTasksTab(_contact: RealEstateContact): void {
  try {
    warmContactTasksCache();
  } catch {
    // preloads are best-effort by contract
  }
}
